/**
 * Executes internal API healthcheck at a configured interval and announces
 * event whenever the status changes.
 */

import Marionette from "backbone.marionette";
import Model from "../models/healthcheck/index";
import Configuration from "../models/healthcheck/config";
import Radio from "backbone.radio";

import { validRoles, delayedStart } from "../config/healthcheck";

const channel = Radio.channel("global");

class Healthcheck extends Marionette.Object {
  initialize() {
    this.timeoutId = null;
    this.userIsEntitled = channel.request("user:has-any-role", validRoles);

    // instantiate configuration model
    this.config = new Configuration();
    // validate configuration, halt when invalid
    if (!this.config.isValid()) {
      return;
    }
    // add listeners
    channel.reply("healthcheck:config", this._getConfig, this);

    // instantiate heartbeat model and listen for changes
    this.model = new Model();
    this.listenTo(this.model, "change", this._onModelChange);

    // bind fetch function to model instance
    this.fetcher = _.bind(this._fetch, this.model, this);

    // start monitor when enabled and user posseses a valid role
    if (this.config.getEnabled() && this.userIsEntitled) {
      this._start();
    }
  }

  _getConfig() {
    return this.config;
  }

  _start() {
    if (delayedStart) {
      // schedule first fetch after interval
      this._scheduleFetch();
    } else {
      // start immediately
      this.fetcher();
    }
  }

  _scheduleFetch() {
    const wait = this.config.getIntervalMillis();

    if (this.timeoutId !== null && _.isNumber(this.timeoutId)) {
      // clear previous timeout
      window.clearTimeout(this.timeoutId);
    }

    // set delay - pass 'this' context to fetcher
    this.timeoutId = _.delay(this.fetcher, wait);
  }

  /**
   * Fetches model data. Schedules next fetch upon successful completion
   *
   * @param {Class instance} context
   */
  _fetch(context) {
    // fetch and handle promise ($.deferredObject)
    const promise = this.fetch();

    // schedule another fetch on successful completion
    promise.done(() => {
      context._scheduleFetch();
    });

    // log error on failure and stop fetching
    promise.fail((err) => {
      console.error(err.stack);
    });
  }

  _onModelChange(model) {
    channel.trigger("healthcheck:update", model);
  }
}

export { Healthcheck as default };
